import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import EntityPanelSelector, { LAYOUT_MODES } from '@common/components/EntityPanelSelector';
import { loadHostCategories } from '@api/filters';
import * as styles from './HostCategoryFilter.module.css';

/**
 * HostCategoryFilter Component
 * Uses EntityPanelSelector in TWO_COLUMNS mode (no filter dropdown)
 */
function HostCategoryFilter({
  selectedHostCategoryIds,
  onSelectionChange,
}) {
  const { t } = useTranslation();
  const [availableHostCategories, setAvailableHostCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load host categories
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const hostCategories = await loadHostCategories();
        setAvailableHostCategories(hostCategories);
      } catch (error) {
        console.error('Error loading host categories:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Labels for EntityPanelSelector
  const labels = useMemo(() => ({
    leftPanelTitle: t('reports.filters.availableEntities'),
    rightPanelTitle: t('reports.filters.selectedEntities'),
    selectAll: t('common.selectAll'),
    clearAll: t('common.clearAll'),
    noItemsAvailable: t('reports.filters.noHostCategoriesAvailable'),
    noItemsFound: t('reports.filters.noHostCategoriesFound'),
    noItemsSelected: t('reports.filters.noEntitiesSelected'),
  }), [t]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <EntityPanelSelector
      mode={LAYOUT_MODES.TWO_COLUMNS}
      availableItems={availableHostCategories}
      selectedItemIds={selectedHostCategoryIds}
      onSelectionChange={onSelectionChange}
      labels={labels}
      valueField="id"
    />
  );
}

HostCategoryFilter.propTypes = {
  selectedHostCategoryIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
};

export { HostCategoryFilter };
export default HostCategoryFilter;
